import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Type, RotateCw, Trash2, MousePointerClick, X } from "lucide-react";

const MODES = [
  { id: "text", label: "Add text", icon: Type },
  { id: "rotate", label: "Rotate page", icon: RotateCw },
  { id: "delete", label: "Delete page", icon: Trash2 },
];

const SIZES = [
  { id: 9, label: "S" },
  { id: 12, label: "M" },
  { id: 16, label: "L" },
];

export const EditTools = ({ thumbs, loading, totalPages, truncated, spec, onSpecChange, fileCount }) => {
  const [mode, setMode] = useState("text");
  const [textValue, setTextValue] = useState("");
  const [size, setSize] = useState(12);
  const [hint, setHint] = useState(null);
  const textInputRef = useRef(null);
  const hintTimer = useRef(null);
  const dragRef = useRef(null);
  const suppressClick = useRef(false);

  // Drag-to-reposition placed text: pointer capture + 5px threshold so a
  // plain click still removes the chip, a drag moves it pixel-perfectly.
  const onChipPointerDown = (t) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { idx: spec.texts.indexOf(t), lastX: e.clientX, lastY: e.clientY, moved: false, x: t.x, y: t.y };
  };
  const onChipPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.idx < 0) return;
    const dx = e.clientX - d.lastX;
    const dy = e.clientY - d.lastY;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
    d.moved = true;
    d.lastX = e.clientX;
    d.lastY = e.clientY;
    const box = e.currentTarget.parentElement.getBoundingClientRect();
    d.x = Math.min(Math.max(d.x + dx / box.width, 0), 0.9);
    d.y = Math.min(Math.max(d.y + dy / box.height, 0), 0.95);
    update({ texts: spec.texts.map((x, xi) => (xi === d.idx ? { ...x, x: d.x, y: d.y } : x)) });
  };
  const onChipPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.moved) suppressClick.current = true;
  };
  const onChipClick = (t) => (e) => {
    e.stopPropagation();
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    update({ texts: spec.texts.filter((x) => x !== t) });
  };

  useEffect(() => {
    if (mode === "text") textInputRef.current?.focus();
  }, [mode]);

  useEffect(() => () => clearTimeout(hintTimer.current), []);

  const flashHint = (msg) => {
    setHint(msg);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(null), 2600);
  };

  const update = (patch) => onSpecChange({ ...spec, ...patch });

  const clickPage = (pageNo, e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 0.9);
    const y = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 0.95);
    if (mode === "text") {
      const content = textValue.trim();
      if (!content) {
        flashHint("Type your text above first, then tap a page to place it");
        textInputRef.current?.focus();
        return;
      }
      update({ texts: [...spec.texts, { page: pageNo, x, y, w: 0.5, content, size }] });
    } else if (mode === "rotate") {
      const next = ((spec.rotate[pageNo] || 0) + 90) % 360;
      const rotate = { ...spec.rotate };
      if (next === 0) delete rotate[pageNo];
      else rotate[pageNo] = next;
      update({ rotate });
    } else {
      const del = spec.delete.includes(pageNo)
        ? spec.delete.filter((p) => p !== pageNo)
        : [...spec.delete, pageNo];
      update({ delete: del });
    }
  };

  const counts = spec.texts.length + Object.keys(spec.rotate).length + spec.delete.length;

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="px-5 pt-4 overflow-hidden" data-testid="edit-panel">
      <div className="border border-hairline rounded-lg bg-page p-3">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex gap-1 p-0.5 bg-white border border-hairline rounded-md" data-testid="edit-mode-picker">
            {MODES.map((m) => (
              <button key={m.id} type="button" data-testid={`edit-mode-${m.id}`} onClick={() => setMode(m.id)}
                className={`flex items-center gap-1.5 px-3 min-h-[44px] rounded-full font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${mode === m.id ? "bg-ink text-white" : "text-fog hover:text-ink"}`}>
                <m.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {m.label}
              </button>
            ))}
          </div>
          <span className="font-mono text-[10px] text-fog" data-testid="edit-count">
            {loading ? "Rendering…" : counts ? `${counts} edit${counts === 1 ? "" : "s"} ready` : `${thumbs.length}${truncated ? ` of ${totalPages}` : ""} pages`}
          </span>
        </div>

        {mode === "text" && (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <input
                ref={textInputRef}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                placeholder="Text to place — then tap a page"
                maxLength={200}
                data-testid="edit-text-input"
                className="flex-1 min-w-0 border border-hairline rounded-lg bg-white px-3.5 py-2 font-mono text-xs text-ink placeholder:text-mist focus:outline-none focus:border-azure transition-colors duration-300"
              />
              <div className="flex gap-1 p-0.5 bg-white border border-hairline rounded-md" data-testid="edit-size-picker">
                {SIZES.map((s) => (
                  <button key={s.id} type="button" data-testid={`edit-size-${s.id}`} onClick={() => setSize(s.id)}
                    className={`px-2.5 min-h-[44px] rounded-full font-mono text-[10px] transition-colors duration-200 ${size === s.id ? "bg-ink text-white" : "text-fog hover:text-ink"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {textValue.trim() && (
              <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-azure" data-testid="edit-pending-hint">
                <MousePointerClick className="w-3.5 h-3.5" strokeWidth={1.5} />
                Placing “{textValue.trim().slice(0, 32)}{textValue.trim().length > 32 ? "…" : ""}” — tap any page
              </p>
            )}
            {hint && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[#FF3333]" data-testid="edit-empty-hint">{hint}</p>
            )}
          </div>
        )}
        {mode === "rotate" && <p className="font-mono text-[10px] text-fog mb-3">Tap a page to rotate it 90°. Tap again for 180°, then 270°.</p>}
        {mode === "delete" && <p className="font-mono text-[10px] text-fog mb-3">Tap a page to mark it for deletion. Tap again to keep it.</p>}

        {loading && (
          <div className="flex gap-3 overflow-hidden" data-testid="edit-thumbs-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-36 w-28 shrink-0 rounded-md bg-white border border-hairline animate-pulse" />)}
          </div>
        )}
        {!loading && thumbs.length > 0 && (
          <div className="grid grid-cols-2 gap-3 max-h-[520px] overflow-y-auto pr-1 bg-[#1F1F1F] rounded-sm p-3" data-testid="edit-canvas">
            {thumbs.map((p) => {
              const deg = spec.rotate[p.page] || 0;
              const isDeleted = spec.delete.includes(p.page);
              const pageTexts = spec.texts.filter((t) => t.page === p.page);
              return (
                <div key={p.page} data-testid={`edit-page-${p.page}`}>
                  <div
                    onClick={(e) => clickPage(p.page, e)}
                    className={`relative overflow-hidden rounded-md border bg-white shadow-[0_2px_16px_-8px_rgba(0,0,0,0.12)] flex items-center justify-center transition-shadow duration-200 ${
                      isDeleted ? "border-[#FF3333]/60 opacity-40" : "border-hairline"
                    } ${
                      mode === "text"
                        ? "cursor-crosshair hover:ring-2 hover:ring-azure/40"
                        : mode === "rotate"
                          ? "cursor-pointer hover:ring-2 hover:ring-azure/40"
                          : "cursor-pointer hover:ring-2 hover:ring-[#FF3333]/40"
                    }`}
                    style={deg % 180 ? { aspectRatio: `${p.height}/${p.width}` } : undefined}
                  >
                    <img src={`data:image/png;base64,${p.image}`} alt={`Page ${p.page}`} draggable={false}
                      className="block select-none pointer-events-none"
                      style={deg ? { transform: `rotate(${deg}deg)`, width: deg % 180 ? `${(p.width / p.height) * 100}%` : "100%", maxWidth: "none" } : { width: "100%" }} />
                    {pageTexts.map((t, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`edit-text-chip-${p.page}-${i}`}
                        title="Drag to move · click to remove"
                        onPointerDown={onChipPointerDown(t)}
                        onPointerMove={onChipPointerMove}
                        onPointerUp={onChipPointerUp}
                        onClick={onChipClick(t)}
                        className="group/chip absolute flex items-center gap-1 bg-azure/90 text-white rounded px-1.5 py-0.5 font-mono text-[9px] hover:bg-[#FF3333] transition-colors duration-200 max-w-[70%] cursor-grab active:cursor-grabbing touch-none select-none"
                        style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
                      >
                        <span className="truncate">{t.content}</span>
                        <X className="w-2.5 h-2.5 shrink-0 opacity-70 group-hover/chip:opacity-100" strokeWidth={2.5} />
                      </button>
                    ))}
                    {deg > 0 && (
                      <span className="absolute top-1.5 right-1.5 bg-ink text-white rounded-full px-2 py-0.5 font-mono text-[9px]" data-testid={`edit-rotate-badge-${p.page}`}>
                        {deg}°
                      </span>
                    )}
                    {isDeleted && (
                      <span className="absolute inset-0 flex items-center justify-center" data-testid={`edit-delete-badge-${p.page}`}>
                        <span className="bg-[#FF3333] text-white rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em]">Deleted</span>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-mist">Page {p.page}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {truncated && !loading && (
          <p className="font-mono text-[10px] text-mist mt-3">Preview shows the first 30 pages. Edits apply to the full document.</p>
        )}
        {counts > 0 && (
          <button type="button" data-testid="edit-clear" onClick={() => onSpecChange({ texts: [], rotate: {}, delete: [] })}
            className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:text-[#FF3333] transition-colors duration-300">
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Clear all edits
          </button>
        )}
      </div>
    </motion.div>
  );
};
